import { Link } from 'react-router-dom';
import { Facebook, Instagram, Youtube, Calendar, ExternalLink, Phone, MessageSquare } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_simple-realty-hub/artifacts/gydjscwr_attachment_1.jpeg";

const socialLinks = [
  { name: 'Facebook (SeanTheBroker)', url: 'https://www.facebook.com/share/17TFPDfMFA/', icon: Facebook },
  { name: 'Facebook (Sean South Florida)', url: 'https://www.facebook.com/profile.php?id=61574448566441&sk=about', icon: Facebook },
  { name: 'Instagram (Straight4TheTop)', url: 'https://www.instagram.com/straight4thetop2', icon: Instagram },
  { name: 'Instagram (SeanTheBroker)', url: 'https://www.instagram.com/seanthebroker1', icon: Instagram },
  { name: 'YouTube', url: 'https://www.youtube.com/@seanmcclain2855', icon: Youtube },
  { name: 'TikTok', url: 'https://www.tiktok.com/@straight4thetop', icon: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
  )},
  { name: 'Calendly', url: 'https://calendly.com/seantherealtor/15min', icon: Calendar },
];

const brandLinks = [
  { abbr: 'SRI', name: 'Real Estate', path: '/buyers' },
  { abbr: 'SMF', name: 'Mortgage Financing', path: '/financing' },
  { abbr: 'SRIM', name: 'Property Management', path: '/property-management' },
  { abbr: 'CCS', name: 'Notary & Closing', path: '/notary' },
  { abbr: 'MMF', name: 'Repairs & Maintenance', path: '/contact' },
];

const quickLinks = [
  { name: 'Buy a Home', path: '/buyers' },
  { name: 'Sell Your Home', path: '/sellers' },
  { name: 'Invest', path: '/investors' },
  { name: 'Get Pre-Approved', path: '/financing' },
  { name: 'Open Houses', path: '/open-houses' },
];

const companyLinks = [
  { name: 'Our Team', path: '/team' },
  { name: 'Join Our Team', path: '/join' },
  { name: 'Contact', path: '/contact' },
  { name: 'Resources', path: '/resources' },
];

export const Footer = () => {
  return (
    <footer className="footer-section py-16" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Brand Ecosystem Bar */}
        <div className="flex flex-wrap justify-center gap-4 mb-12 pb-10 border-b border-[#D4AF37]/10">
          {brandLinks.map((brand) => (
            <Link
              key={brand.abbr}
              to={brand.path}
              className="text-center group"
              data-testid={`footer-brand-${brand.abbr.toLowerCase()}`}
            >
              <span className="text-[#D4AF37] font-bold text-sm group-hover:text-[#F5E6A3] transition-colors">{brand.abbr}</span>
              <span className="text-[#808080] text-[10px] block">{brand.name}</span>
            </Link>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <img src={LOGO_URL} alt="Simple Realty & Investments™" className="h-14 w-auto mb-4" />
            <p className="text-[#D4AF37] text-sm font-semibold mb-2">One Call Handles It All</p>
            <p className="text-[#808080] text-sm leading-relaxed mb-4">
              Buy, sell, invest, finance, manage, and maintain — all under one roof.
            </p>
            <div className="space-y-2 mb-4">
              <a href="tel:7863560878" className="flex items-center text-[#D4AF37] hover:text-[#F5E6A3] text-sm font-semibold">
                <Phone className="w-4 h-4 mr-2" /> (786) 356-0878
              </a>
              <a href="sms:7863560878" className="flex items-center text-[#808080] hover:text-[#D4AF37] text-sm">
                <MessageSquare className="w-4 h-4 mr-2" /> Text Us
              </a>
            </div>
            <div className="flex flex-wrap gap-2">
              {socialLinks.map((social) => (
                <a key={social.name} href={social.url} target="_blank" rel="noopener noreferrer" className="social-icon" title={social.name}
                  data-testid={`footer-social-${social.name.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Funnels */}
          <div>
            <h4 className="text-[#F5F5F0] font-semibold mb-6 text-sm uppercase tracking-wider">I Want To...</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-[#808080] hover:text-[#D4AF37] transition-colors text-sm">{link.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-[#F5F5F0] font-semibold mb-6 text-sm uppercase tracking-wider">Company</h4>
            <ul className="space-y-3">
              {companyLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-[#808080] hover:text-[#D4AF37] transition-colors text-sm">{link.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-[#F5F5F0] font-semibold mb-6 text-sm uppercase tracking-wider">Resources</h4>
            <ul className="space-y-3">
              <li>
                <a href="https://stan.store/straight4thetop" target="_blank" rel="noopener noreferrer" className="text-[#808080] hover:text-[#D4AF37] transition-colors text-sm flex items-center" data-testid="footer-stan-store">
                  Stan Store <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </li>
              <li>
                <a href="https://straight4thetop.gumroad.com" target="_blank" rel="noopener noreferrer" className="text-[#808080] hover:text-[#D4AF37] transition-colors text-sm flex items-center" data-testid="footer-gumroad">
                  Gumroad Products <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </li>
              <li>
                <a href="https://calendly.com/seantherealtor/15min" target="_blank" rel="noopener noreferrer" className="text-[#808080] hover:text-[#D4AF37] transition-colors text-sm flex items-center" data-testid="footer-calendly">
                  Book Consultation <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </li>
            </ul>
            <div className="mt-6 pt-6 border-t border-[#D4AF37]/10">
              <p className="text-[#808080] text-xs">South Florida</p>
              <a href="mailto:info@simplerealtyandinvestments.com" className="text-[#D4AF37] text-sm hover:underline">
                info@simplerealtyandinvestments.com
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-[#D4AF37]/10 flex flex-col md:flex-row justify-between items-center">
          <div className="text-center md:text-left mb-4 md:mb-0">
            <p className="text-[#808080] text-xs">
              Simple Realty &amp; Investments&trade; is a trademark of The McClain Group&trade;.
            </p>
            <p className="text-[#808080] text-xs">
              &copy; {new Date().getFullYear()} The McClain Group&trade;. All rights reserved.
            </p>
          </div>
          <div className="flex space-x-6">
            <Link to="/admin" className="text-[#808080] hover:text-[#D4AF37] text-xs" data-testid="footer-admin-link">Admin Portal</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
